import React, { Fragment, useEffect, useRef } from 'react';
import type { TraceStepDetail } from '@shared/trace';
import { Badge, VerdictBadge, formatDuration } from '../design/primitives';

/**
 * Renderers for each stage's detail payload.
 *
 * Each one answers a different question, so they deliberately do not share a
 * generic shape. Canonicalisation needs a side-by-side; the control-plane call
 * needs a wire dump; execution needs a terminal. Forcing those into one "detail
 * table" component would make all three worse.
 */
export function StageDetail({ detail }: { detail: TraceStepDetail }) {
  switch (detail.kind) {
    case 'composed':
      return <Composed detail={detail} />;
    case 'canonicalized':
      return <Canonicalized detail={detail} />;
    case 'offline_precheck':
      return <Precheck detail={detail} />;
    case 'request':
      return <Http detail={detail} />;
    case 'shield':
      return <Shield detail={detail} />;
    case 'policy':
      return <Policy detail={detail} />;
    case 'decision':
      return <Decision detail={detail} />;
    case 'approval':
      return <Approval detail={detail} />;
    case 'execution':
      return <Execution detail={detail} />;
    case 'response_scan':
      return <ResponseScan detail={detail} />;
    case 'evidence':
      return <Evidence detail={detail} />;
    default:
      return null;
  }
}

function KeyValues({ rows }: { rows: Array<[string, React.ReactNode]> }) {
  const visible = rows.filter(([, value]) => value !== undefined && value !== null && value !== '');
  if (visible.length === 0) return null;
  return (
    <dl className="th-kv">
      {visible.map(([label, value]) => (
        <Fragment key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </Fragment>
      ))}
    </dl>
  );
}

function Composed({ detail }: { detail: Extract<TraceStepDetail, { kind: 'composed' }> }) {
  return (
    <div className="th-detail">
      <KeyValues
        rows={[
          ['Operator', `${detail.operatorName} — ${detail.operatorRole}`],
          ['Identity', <code key="i">{detail.developerName}</code>],
          ['Host', `${detail.hostname}${detail.ipAddress ? ` · ${detail.ipAddress}` : ''}`],
          ['Agent', <code key="a">{detail.agentName}</code>],
          ['Tool', <code key="t">{detail.mcpServer ? `${detail.mcpServer} · ${detail.toolName}` : detail.toolName}</code>],
          ['Working dir', detail.cwd ? <code key="c">{detail.cwd}</code> : undefined],
        ]}
      />
      {Object.keys(detail.args).length > 0 && (
        <pre className="th-pre" style={{ marginTop: 'var(--s-2)' }}>
          {JSON.stringify(detail.args, null, 2)}
        </pre>
      )}
    </div>
  );
}

function Canonicalized({ detail }: { detail: Extract<TraceStepDetail, { kind: 'canonicalized' }> }) {
  if (!detail.changed) {
    return (
      <div className="th-detail">
        <pre className="th-pre">{detail.raw}</pre>
      </div>
    );
  }
  return (
    <div className="th-detail">
      <div className="th-diff">
        <div className="th-diff__side">
          <p className="th-diff__label">As written</p>
          <pre className="th-pre">{detail.raw}</pre>
        </div>
        <div className="th-diff__side th-diff__side--resolved">
          <p className="th-diff__label">What it resolves to</p>
          <pre className="th-pre">{detail.canonical}</pre>
        </div>
      </div>
      <div className="th-chips">
        {detail.transformations.map(transformation => (
          <Badge key={transformation} tone="info">
            {transformation}
          </Badge>
        ))}
      </div>
    </div>
  );
}

function Precheck({ detail }: { detail: Extract<TraceStepDetail, { kind: 'offline_precheck' }> }) {
  return (
    <div className="th-detail">
      <KeyValues
        rows={[
          ['Verdict', <VerdictBadge key="v" verdict={detail.verdict} />],
          ['Policy', detail.matchedPolicyName],
          ['Rule', detail.matchedRule ? <code key="r">{detail.matchedRule}</code> : undefined],
          ['Operation', <code key="o">{detail.inferredOperation}</code>],
          [
            'Policies cached',
            `${detail.policiesConsidered.length} (${detail.policiesConsidered.reduce(
              (total, policy) => total + policy.ruleCount,
              0,
            )} rules) · bundle ${detail.policyHash}`,
          ],
          ['How', detail.note],
        ]}
      />
      {detail.monitorMatches.length > 0 && (
        <p className="th-note">
          {detail.monitorMatches
            .map(match => `${match.policyName} would have been ${match.verdict.replace('_', ' ')}`)
            .join('; ')}
        </p>
      )}
    </div>
  );
}

function Http({ detail }: { detail: Extract<TraceStepDetail, { kind: 'request' }> }) {
  const status = detail.responseStatus ?? 0;
  const ok = status >= 200 && status < 300;
  return (
    <div className="th-detail th-http">
      <div className="th-http__bar">
        <span className="th-http__method">{detail.method}</span>
        <span className="th-http__url">{detail.url}</span>
        <span className="th-http__status" data-ok={String(ok)}>
          {detail.responseStatus ?? 'no response'}
          {detail.durationMs !== undefined && ` · ${formatDuration(detail.durationMs)}`}
        </span>
      </div>
      <pre className="th-pre">
        {Object.entries(detail.requestHeaders)
          .map(([key, value]) => `${key}: ${value}`)
          .join('\n')}
        {'\n\n'}
        {JSON.stringify(detail.requestBody, null, 2)}
      </pre>
      {detail.responseBody !== undefined && (
        <pre className="th-pre">{JSON.stringify(detail.responseBody, null, 2)}</pre>
      )}
    </div>
  );
}

function Shield({ detail }: { detail: Extract<TraceStepDetail, { kind: 'shield' }> }) {
  return (
    <div className="th-detail">
      <pre className="th-pre" style={{ marginBottom: 'var(--s-2)' }}>
        {detail.scanned}
      </pre>
      <div className="th-tiers">
        {detail.tiers.map(tier => (
          <div
            key={tier.id}
            className="th-tier"
            data-status={tier.verdict}
            data-deciding={String(tier.id === detail.decidingTierId)}
            title={tier.description}
          >
            <span className="th-tier__mark" aria-hidden>
              {tier.verdict === 'skipped' ? '\u2014' : tier.id === detail.decidingTierId ? '\u25C6' : '\u2713'}
            </span>
            <span>{tier.label}</span>
            <span className="th-tier__latency">{tier.latencyMs !== undefined ? `${tier.latencyMs} ms` : ''}</span>
            {tier.note && <span className="th-tier__note">{tier.note}</span>}
          </div>
        ))}
      </div>
      <div style={{ marginTop: 'var(--s-2)' }}>
        <KeyValues
          rows={[
            ['Analysis path', detail.analysisPath ? <code key="p">{detail.analysisPath}</code> : undefined],
            ['Confidence', detail.confidence !== undefined ? `${Math.round(detail.confidence * 100)}%` : undefined],
            ['Reason', detail.reason],
            ['Latency', detail.totalLatencyMs !== undefined ? formatDuration(detail.totalLatencyMs) : undefined],
          ]}
        />
      </div>
    </div>
  );
}

function Policy({ detail }: { detail: Extract<TraceStepDetail, { kind: 'policy' }> }) {
  return (
    <div className="th-detail">
      <KeyValues
        rows={[
          ['Verdict', <VerdictBadge key="v" verdict={detail.verdict} />],
          ['Policy', detail.policyName],
          ['Matched rule', detail.matchedRule ? <code key="r">{detail.matchedRule}</code> : undefined],
          ['Reason', detail.reason],
          [
            'Offline check',
            detail.agreesWithOffline
              ? 'Agrees with the cached engine — this machine would decide the same with no network.'
              : 'Differs from the cached verdict; the control plane is authoritative.',
          ],
        ]}
      />
      {detail.monitorMatches.length > 0 && (
        <div style={{ marginTop: 'var(--s-2)' }}>
          <p className="th-diff__label">Also matched, in monitor mode</p>
          {detail.monitorMatches.map(match => (
            <p key={match.policyName} className="th-note" style={{ marginTop: 2 }}>
              {match.policyName} — would have been {match.verdict.replace('_', ' ')} ({match.matchedRule})
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

function Decision({ detail }: { detail: Extract<TraceStepDetail, { kind: 'decision' }> }) {
  if (!detail.guidance) return null;
  return (
    <div className="th-detail">
      <p className="th-note" style={{ marginTop: 0 }}>
        {detail.guidance}
      </p>
    </div>
  );
}

function Approval({ detail }: { detail: Extract<TraceStepDetail, { kind: 'approval' }> }) {
  return (
    <div className="th-detail">
      <KeyValues
        rows={[
          ['Routed to', detail.routedTo.join(', ')],
          ['Action id', detail.actionId ? <code key="a">{detail.actionId}</code> : undefined],
          ['Resolved by', detail.resolvedBy],
        ]}
      />
      {detail.note && <p className="th-note">{detail.note}</p>}
    </div>
  );
}

function Execution({ detail }: { detail: Extract<TraceStepDetail, { kind: 'execution' }> }) {
  const ref = useRef<HTMLDivElement>(null);

  // Follow the output as it streams, the way a terminal does.
  useEffect(() => {
    if (ref.current) ref.current.scrollTop = ref.current.scrollHeight;
  }, [detail.lines.length]);

  return (
    <div className="th-detail">
      <div className="th-term" ref={ref} role="log" aria-live="polite" aria-label="Command output">
        {detail.lines.map((line, index) => (
          <div key={index} className="th-term__line" data-stream={line.stream}>
            {line.text}
          </div>
        ))}
        {detail.exitCode === undefined && <div className="th-term__line th-term__caret" />}
      </div>
    </div>
  );
}

function ResponseScan({ detail }: { detail: Extract<TraceStepDetail, { kind: 'response_scan' }> }) {
  return (
    <div className="th-detail">
      {detail.findings.map(finding => (
        <div key={finding.label} className="th-finding">
          <strong>{finding.label}</strong>
          <span>{finding.detail}</span>
        </div>
      ))}
      {detail.sanitizedPreview && <p className="th-note">{detail.sanitizedPreview}</p>}
      <p className="th-note">
        {detail.scannedBytes} bytes inspected before this text was allowed back into the agent's context.
      </p>
    </div>
  );
}

function Evidence({ detail }: { detail: Extract<TraceStepDetail, { kind: 'evidence' }> }) {
  return (
    <div className="th-detail">
      <KeyValues
        rows={[
          ['Action id', detail.actionId ? <code key="a">{detail.actionId}</code> : undefined],
          ['Recorded in', detail.recordedSurfaces.join(' · ')],
        ]}
      />
      {detail.dashboardUrl && (
        <p className="th-note">
          <a href={detail.dashboardUrl} target="_blank" rel="noreferrer noopener">
            Open this action in AgentGuard →
          </a>
        </p>
      )}
    </div>
  );
}
